/**
 * 患者一覧ページ
 *
 * CRUD操作のフロントエンド実装サンプル
 * - 一覧表示（Read）
 * - 新規作成（Create）
 * - 削除（Delete）
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApi, useMutation } from "../hooks/useApi";
import api from "../services/api";
import { offlineStorage } from "../services/offlineStorage";
import { syncPendingPatientCreates } from "../services/syncService";
import type { CreatePatientRequest, Patient } from "../types";

function PatientsPage() {
  // ========================================
  // 状態管理
  // ========================================
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<CreatePatientRequest>({
    patientCode: "",
    name: "",
    nameKana: "",
    memo: "",
  });
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [pendingLocalPatients, setPendingLocalPatients] = useState<Patient[]>([]);
  const [notice, setNotice] = useState<string | null>(null);

  // ========================================
  // API呼び出し
  // ========================================
  // 患者一覧を取得
  const {
    data: patients,
    loading,
    error,
    refetch,
  } = useApi<Patient[]>("/api/patients");

  const visiblePatients = [
    ...(patients ?? []).filter((patient) => !patient.isDeleted),
    ...pendingLocalPatients,
  ];

  // 患者作成用のミューテーション
  const createMutation = useMutation<Patient, CreatePatientRequest>(
    "/api/patients",
    "POST",
  );

  // ========================================
  // イベントハンドラ
  // ========================================

  // フォーム入力の処理
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 患者作成の処理
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const normalizedPayload: CreatePatientRequest = {
      patientCode: formData.patientCode.trim(),
      name: formData.name.trim(),
      nameKana: formData.nameKana?.trim() || "",
      memo: formData.memo?.trim() || "",
    };

    if (!isOnline) {
      const tempId = `offline-patient-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      await offlineStorage.savePendingPatientCreate({
        tempId,
        payload: normalizedPayload,
      });

      const now = new Date().toISOString();
      const localPatient: Patient = {
        id: tempId,
        facilityId: "offline",
        patientCode: normalizedPayload.patientCode,
        name: normalizedPayload.name,
        nameKana: normalizedPayload.nameKana || null,
        memo: normalizedPayload.memo || null,
        isDeleted: false,
        createdAt: now,
        updatedAt: now,
      };
      setPendingLocalPatients((prev) => [localPatient, ...prev]);
      setNotice("オフラインで患者を保存しました。オンライン復帰時に自動同期されます。");
      setFormData({ patientCode: "", name: "", nameKana: "", memo: "" });
      setShowForm(false);
      return;
    }

    const result = await createMutation.mutate(normalizedPayload);
    if (!result) {
      return;
    }

    // 成功したらフォームをリセットして一覧を再取得
    setFormData({ patientCode: "", name: "", nameKana: "", memo: "" });
    setShowForm(false);
    setNotice(null);
    refetch();
  };

  // 患者削除の処理
  const handleDelete = async (id: string, name: string) => {
    if (id.startsWith("offline-patient-")) {
      if (!confirm(`「${name}」を削除しますか？`)) {
        return;
      }
      await offlineStorage.deletePendingPatientCreateByTempId(id);
      setPendingLocalPatients((prev) => prev.filter((patient) => patient.id !== id));
      return;
    }

    if (!navigator.onLine) {
      window.alert("現在オフラインのため、患者を削除できません。");
      return;
    }

    if (!confirm(`「${name}」を削除しますか？`)) {
      return;
    }

    try {
      await api.delete(`/api/patients/${id}`);
      refetch();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const mapPendingToPatient = (tempId: string, payload: CreatePatientRequest, savedAt: string): Patient => ({
    id: tempId,
    facilityId: "offline",
    patientCode: payload.patientCode,
    name: payload.name,
    nameKana: payload.nameKana || null,
    memo: payload.memo || null,
    isDeleted: false,
    createdAt: savedAt,
    updatedAt: savedAt,
  });

  useEffect(() => {
    offlineStorage
      .getAllPendingPatientCreates()
      .then((items) => {
        if (items.length === 0) return;
        setPendingLocalPatients(
          items
            .map((item) => mapPendingToPatient(item.tempId, item.payload, item.savedAt))
            .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)),
        );
      })
      .catch((error) => {
        console.error("Failed to load offline patients:", error);
      });
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!isOnline) return;

    syncPendingPatientCreates()
      .then((result) => {
        if (result.synced > 0) {
          offlineStorage
            .getAllPendingPatientCreates()
            .then((items) => {
              setPendingLocalPatients(
                items
                  .map((item) => mapPendingToPatient(item.tempId, item.payload, item.savedAt))
                  .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)),
              );
            })
            .catch((error) => {
              console.error("Failed to refresh offline patients:", error);
            });
          setNotice(null);
          refetch();
        }
      })
      .catch((error) => {
        console.error("Patient sync on reconnect failed:", error);
      });
  }, [isOnline, refetch]);

  // ========================================
  // ローディング・エラー表示
  // ========================================
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h2 className="text-red-800 font-semibold mb-2">
          エラーが発生しました
        </h2>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={refetch}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          再試行
        </button>
      </div>
    );
  }

  // ========================================
  // メインのレンダリング
  // ========================================
  return (
    <div>
      {notice && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          <p className="text-amber-800 text-sm">{notice}</p>
        </div>
      )}

      {/* ページヘッダー */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">患者一覧</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          新規登録
        </button>
      </div>

      {/* 新規登録フォーム */}
      {showForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">患者登録</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            {createMutation.error && (
              <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
                <p className="text-red-600 text-sm">{createMutation.error}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  患者コード <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="patientCode"
                  value={formData.patientCode}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="P001"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  氏名 
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="山田太郎"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                フリガナ
              </label>
              <input
                type="text"
                name="nameKana"
                value={formData.nameKana}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="ヤマダタロウ"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                メモ
              </label>
              <textarea
                name="memo"
                value={formData.memo}
                onChange={handleInputChange}
                rows={2}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                キャンセル
              </button>
              <button
                type="submit"
                disabled={createMutation.loading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {createMutation.loading ? "登録中..." : "登録"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 患者一覧 */}
      {visiblePatients.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
          患者が登録されていません
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  患者コード
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  氏名
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  フリガナ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  登録日
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {visiblePatients.map((patient) => (
                <tr key={patient.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {patient.patientCode}
                    {patient.id.startsWith("offline-patient-") && (
                      <span className="ml-2 text-xs text-amber-600">未同期</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {patient.id.startsWith("offline-patient-") ? (
                      <span className="text-gray-900">{patient.name}</span>
                    ) : (
                      <Link
                        to={`/patients/${patient.id}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {patient.name}
                      </Link>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {patient.nameKana || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(patient.createdAt).toLocaleDateString("ja-JP")}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <button
                      onClick={() => handleDelete(patient.id, patient.name)}
                      className="px-2 py-1 rounded border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-800 transition-colors"
                    >
                      削除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 件数表示 */}
      {visiblePatients.length > 0 && (
        <div className="mt-4 text-sm text-gray-500 text-right">
          全 {visiblePatients.length} 件
        </div>
      )}
    </div>
  );
}

export default PatientsPage;
